/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   jobService.ts
 *     Feature         :   114-job-infrastructure
 *     Description     :   Frontend API service for job infrastructure endpoints
 *     Constitution    :   v1.11.0 | Principle XXXVIII (Specification Testing Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-28
 * =========================================================================================
 */

import { apiClient } from './apiClient';

// API Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
}

// ============================================================================
// Types
// ============================================================================

export type JobType = 'collection' | 'backup' | 'restore' | 'retention';

export type JobRunStatus =
  | 'PENDING'
  | 'QUEUED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'TIMEOUT'
  | 'CANCELLED'
  | 'ACR_IMAGE_PULL_FAILED';

export type JobTriggerType = 'manual' | 'scheduled' | 'retry';

export type LogSeverity = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL';

export interface JobDefinition {
  id: string;
  name: string;
  job_name?: string; // Alias for name
  description: string | null;
  job_type: JobType;
  container_image: string;
  cpu_cores: number;
  memory_gb: number;
  timeout_minutes: number;
  max_retries: number;
  default_schedule: string | null;
  default_parameters?: Record<string, unknown> | null;
  notification_email: string | null;
  notification_webhook_url: string | null;
  notification_webhook_type: 'slack' | 'teams' | null;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface JobRun {
  id: string;
  run_id: string;
  job_definition_id: string;
  job_definition?: Partial<JobDefinition>;
  status: JobRunStatus;
  started_at: string | null;
  completed_at: string | null;
  last_heartbeat_at: string | null;
  duration_seconds: number | null;
  error_message: string | null;
  retry_count: number;
  triggered_by: string | null;
  trigger_type: JobTriggerType;
  capacity_id: string | null;
  capacity_type_code: string | null;
  client_id: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  container_instance_id: string | null;
  // Extended properties for UI
  correlation_id?: string | null;
  parameters?: Record<string, unknown> | null;
  container_group_name?: string | null;
  exit_code?: number | null;
  created_at: string;
  updated_at: string;
}

export interface JobLog {
  id: number;
  job_run_id: string;
  timestamp: string;
  severity: LogSeverity;
  message: string;
  context: Record<string, unknown> | null;
  created_at: string;
}

export type JobScheduleCadence = 'daily' | '2x-daily' | '4x-daily' | 'hourly' | '30-min' | '15-min' | '5-min';

/** What a job is doing for the organization: asked for, waiting for the worker, or running. */
export type JobRunState = 'requested' | 'queued' | 'running';

/** One slot of the signed-in organization's collection schedule (dbo.worker_schedules). */
export interface JobSchedule {
  id: string;
  job_identity: string;
  job_name: string;
  cadence: JobScheduleCadence;
  is_active: boolean;
  last_dispatched_at: string | null;
  next_due_at: string | null;
  run_requested_at: string | null;
  run_requested_by: string | null;
  run_state: JobRunState | null;
}

export interface TenantJobSchedule {
  dispatchEnabled: boolean;
  workerRegistered: boolean;
  schedules: JobSchedule[];
  /** Every job on its way, by definition name, the merge included. */
  job_states: Record<string, JobRunState>;
}

/** What Run Now answers: a run row for a merge, or a request for a collection run, which has no row until it starts. */
export interface TriggerJobResult extends Partial<JobRun> {
  queued?: boolean;
  message?: string;
  rateLimitRemaining?: number;
}

export interface JobDashboardSummary {
  total_runs: number;
  running_count: number;
  completed_count: number;
  failed_count: number;
  pending_count: number;
  /** Completed ÷ finished runs in the window; null when no run finished (Spec 298 FR-004). */
  success_rate: number | null;
  by_job_type: Record<JobType, {
    total: number;
    running: number;
    completed: number;
    failed: number;
  }>;
  recent_failures: JobRun[];
}

export interface Pagination {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: Pagination;
}

export interface JobRunFilters {
  job_definition_id?: string;
  status?: JobRunStatus;
  capacity_type_code?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
}

export interface TriggerJobRequest {
  job_definition_id: string;
  capacity_id?: string;
  client_id?: string;
  /** Run a single named collector instead of the full tier (Feature 143) */
  collector_name?: string;
  /** JSON scope override: workspace_id and/or capacity_id filters (Feature 143) */
  scope?: string;
}

// ============================================================================
// Job Definitions API
// ============================================================================

/**
 * Get all job definitions
 */
export async function getJobDefinitions(): Promise<JobDefinition[]> {
  const response = await apiClient.get<ApiResponse<JobDefinition[]>>('/api/jobs/definitions');
  return response.data.data || [];
}

/**
 * Get a specific job definition
 */
export async function getJobDefinition(id: string): Promise<JobDefinition | null> {
  try {
    const response = await apiClient.get<ApiResponse<JobDefinition>>(`/api/jobs/definitions/${id}`);
    return response.data.data || null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// ============================================================================
// Job Runs API
// ============================================================================

/**
 * Get job runs with pagination and filtering
 */
export async function getJobRuns(filters: JobRunFilters = {}): Promise<PaginatedResponse<JobRun>> {
  const params = new URLSearchParams();
  
  if (filters.job_definition_id) params.set('job_definition_id', filters.job_definition_id);
  if (filters.status) params.set('status', filters.status);
  if (filters.capacity_type_code) params.set('capacity_type_code', filters.capacity_type_code);
  if (filters.from_date) params.set('from_date', filters.from_date);
  if (filters.to_date) params.set('to_date', filters.to_date);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.page_size) params.set('page_size', String(filters.page_size));

  const response = await apiClient.get<ApiResponse<PaginatedResponse<JobRun>>>(
    `/api/jobs/runs?${params.toString()}`
  );
  
  return response.data.data || { data: [], pagination: { page: 1, page_size: 20, total_items: 0, total_pages: 0 } };
}

/**
 * Get a specific job run
 */
export async function getJobRun(id: string): Promise<JobRun | null> {
  try {
    const response = await apiClient.get<ApiResponse<JobRun>>(`/api/jobs/runs/${id}`);
    return response.data.data || null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Trigger a manual job run
 */
export async function triggerJobRun(
  jobDefinitionId: string,
  options?: { capacity_id?: string; client_id?: string; collector_name?: string; scope?: string }
): Promise<TriggerJobResult> {
  const request: TriggerJobRequest = {
    job_definition_id: jobDefinitionId,
    ...options,
  };
  
  const response = await apiClient.post<ApiResponse<TriggerJobResult>>('/api/jobs/runs', request);
  
  // Extract rate limit info from headers if available
  const rateLimitRemaining = response.headers?.['x-ratelimit-remaining'];
  
  return {
    ...response.data.data!,
    rateLimitRemaining: rateLimitRemaining ? parseInt(rateLimitRemaining, 10) : undefined,
  };
}

/**
 * Whether Cancel does anything for this run. Only a run with a container can be stopped from here: a worker run
 * has none, and the worker has no stop command yet.
 */
export function canCancelJobRun(job: Pick<JobRun, 'status' | 'container_instance_id'>): boolean {
  return ['PENDING', 'QUEUED', 'RUNNING'].includes(job.status) && !!job.container_instance_id;
}

/**
 * Cancel a running or pending job
 */
export async function cancelJobRun(id: string): Promise<JobRun> {
  const response = await apiClient.post<ApiResponse<JobRun>>(`/api/jobs/runs/${id}/cancel`);
  return response.data.data!;
}

// ============================================================================
// Job Logs API
// ============================================================================

/**
 * Log filter options
 */
export interface JobLogFilters {
  severity?: LogSeverity;
  severities?: LogSeverity[];
  startTime?: string;
  endTime?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Log statistics response
 */
export interface JobLogStats {
  total: number;
  bySeverity: Record<LogSeverity, number>;
  firstLog?: string;
  lastLog?: string;
}

/**
 * Get logs for a specific job run with enhanced filtering
 */
export async function getJobRunLogs(
  jobRunId: string,
  options: JobLogFilters = {}
): Promise<PaginatedResponse<JobLog>> {
  const params = new URLSearchParams();
  
  if (options.severity) params.set('severity', options.severity);
  if (options.severities?.length) params.set('severities', options.severities.join(','));
  if (options.startTime) params.set('startTime', options.startTime);
  if (options.endTime) params.set('endTime', options.endTime);
  if (options.search) params.set('search', options.search);
  if (options.page) params.set('page', String(options.page));
  if (options.pageSize) params.set('pageSize', String(options.pageSize));

  const response = await apiClient.get<ApiResponse<PaginatedResponse<JobLog>>>(
    `/api/jobs/runs/${jobRunId}/logs?${params.toString()}`
  );
  
  return response.data.data || { data: [], pagination: { page: 1, page_size: 50, total_items: 0, total_pages: 0 } };
}

/**
 * Get log statistics for a specific job run
 */
export async function getJobRunLogStats(jobRunId: string): Promise<JobLogStats> {
  const response = await apiClient.get<ApiResponse<JobLogStats>>(
    `/api/jobs/runs/${jobRunId}/logs/stats`
  );
  
  return response.data.data || {
    total: 0,
    bySeverity: { DEBUG: 0, INFO: 0, WARN: 0, ERROR: 0, FATAL: 0 },
  };
}

// ============================================================================
// Job Schedules API
// ============================================================================

/**
 * The signed-in organization's collection schedule. Read only: the brain owns it.
 */
export async function getJobSchedules(): Promise<TenantJobSchedule> {
  const response = await apiClient.get<ApiResponse<TenantJobSchedule>>('/api/jobs/schedules');
  return response.data.data ?? { dispatchEnabled: false, workerRegistered: false, schedules: [], job_states: {} };
}

// ============================================================================
// Dashboard API
// ============================================================================

/**
 * Get job dashboard summary statistics
 */
export async function getJobDashboardSummary(): Promise<JobDashboardSummary> {
  const response = await apiClient.get<ApiResponse<JobDashboardSummary>>('/api/jobs/dashboard/summary');
  return response.data.data!;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: JobRunStatus): string {
  const labels: Record<JobRunStatus, string> = {
    PENDING: 'Pending',
    QUEUED: 'Queued',
    RUNNING: 'Running',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    TIMEOUT: 'Timeout',
    CANCELLED: 'Cancelled',
    ACR_IMAGE_PULL_FAILED: 'Image Pull Failed',
  };
  return labels[status] || status;
}

/**
 * Get status color class for UI styling
 */
export function getStatusColor(status: JobRunStatus): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
    case 'TIMEOUT':
    case 'ACR_IMAGE_PULL_FAILED':
      return 'error';
    case 'RUNNING':
      return 'info';
    case 'PENDING':
    case 'QUEUED':
      return 'warning';
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Get human-readable job type label
 */
export function getJobTypeLabel(jobType: JobType): string {
  const labels: Record<JobType, string> = {
    collection: 'Data Collection',
    backup: 'Lakehouse Backup',
    restore: 'Restore/Recovery',
    retention: 'Retention Cleanup',
  };
  return labels[jobType] || jobType;
}

/**
 * Format duration in seconds to human-readable string
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) {
    return '-';
  }
  
  if (seconds < 60) {
    return `${seconds}s`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format relative time (e.g., "2 minutes ago")
 */
export function formatRelativeTime(dateString: string | null): string {
  if (!dateString) {
    return '-';
  }
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  
  if (diffSeconds < 60) {
    return 'Just now';
  }
  
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
}
